import { ExchangeEmailId } from "client/Api/Email/common";
import { EmailIdWithIsReadStatus } from "client_legacy/Email/emailEvents";
import { ActionKind } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { EmailResponseKind } from "../EmailPage/EmailPage";
import { MessageFlagState, MessageReadState } from "client/Api/Email/email";
import { ActionWithQuickActionIndex } from "client/Components";

export type EmailActionModel = {
	emailId: ExchangeEmailId;
	subject: string | undefined;
	isRead: boolean;
	isFlagged: boolean;
	isDraft: boolean;
	smtpAddress: string | null;
};

export type EmailSelectionActionContext = {
	effectiveSelection: readonly EmailActionModel[];
	selectedItems: readonly EmailActionModel[];
};

export type EmailSelectionAction = ActionWithQuickActionIndex<EmailSelectionActionContext>;

export type EmailSelectionActionHandlers = {
	respondToEmail: (
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	) => void;
	move: (emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null) => void;
	delete: (emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null) => void;
	changeReadStatus: (
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	) => void;
	changeFlagStatus: (
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	) => void;
};

export function getSelectionActions(
	handlers: EmailSelectionActionHandlers,
	onBeforeMoveEmail: () => boolean,
	onBeforeModifyEmail: () => boolean
): readonly EmailSelectionAction[] {

	return [
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_DeleteEmail"),
			icon: "trash-can",
			canExecute: ({ effectiveSelection }) => effectiveSelection.length > 0,
			execute: ({ effectiveSelection }) => {
				if (onBeforeModifyEmail()) {
					handlers.delete(
						effectiveSelection,
						effectiveSelection[0]
							?.smtpAddress
							?? null
					);
				}
			},
			quickActionIndex: 2
		},
		...getResponseActions(handlers.respondToEmail),
		{
			kind: ActionKind.Regular,
			name: "move",
			title: localize("_MoveEmailTo"),
			icon: "arrows-up-down-left-right",
			canExecute: ({ effectiveSelection }) => effectiveSelection.length > 0,
			execute: ({ effectiveSelection }) => {
				if (onBeforeMoveEmail()) {
					handlers.move(
						effectiveSelection,
						effectiveSelection[0]
							?.smtpAddress
							?? null
					);
				}
			}
		},
		{
			kind: ActionKind.Regular,
			name: "markAsUnread",
			title: localize("_MarkAsUnread"),
			icon: "envelope",
			canExecute: ({ effectiveSelection }) => effectiveSelection.length > 0
				&& effectiveSelection.every((x) => x.isRead),
			execute: ({ effectiveSelection }) => {
				if (onBeforeModifyEmail()) {
					handlers.changeReadStatus(
						effectiveSelection.filter((x) => x.isRead)
							.map((x) => x.emailId),
						MessageReadState.Unread,
						effectiveSelection[0]
							?.smtpAddress
							?? null
					);
				}
			},
			quickActionIndex: 0
		},
		{
			kind: ActionKind.Regular,
			name: "markAsRead",
			title: localize("_MarkAsRead"),
			icon: "envelope-open",
			canExecute: ({ effectiveSelection }) => effectiveSelection.length > 0
				&& effectiveSelection.some((x) => !x.isRead),
			execute: ({ effectiveSelection }) => {
				if (onBeforeModifyEmail()) {
					const emailIdsToMarkAsRead: ExchangeEmailId[] = effectiveSelection.filter((x) => !x.isRead)
						.map((x) => x.emailId);
					handlers.changeReadStatus(
						emailIdsToMarkAsRead,
						MessageReadState.Read,
						effectiveSelection[0]
							?.smtpAddress
							?? null
					);
				}
			},
			quickActionIndex: 0
		},
		{
			kind: ActionKind.Regular,
			name: "markAsFlagged",
			title: localize("_Flag"),
			icon: "flag",
			canExecute: ({ effectiveSelection }) => effectiveSelection.length > 0
				&& effectiveSelection.some((x) => !x.isFlagged),
			execute: ({ effectiveSelection }) => {
				if (onBeforeModifyEmail()){
					handlers.changeFlagStatus(
						effectiveSelection.filter((x) => !x.isFlagged)
							.map((x) => x.emailId),
						MessageFlagState.Flagged,
						effectiveSelection[0]
							?.smtpAddress
							?? null
					);
				}
			},
			quickActionIndex: 1
		},
		{
			kind: ActionKind.Regular,
			name: "markAsNotFlagged",
			title: localize("_Unflag"),
			icon: "flag-filled",
			canExecute: ({ effectiveSelection }) => effectiveSelection.length > 0
				&& effectiveSelection.every((x) => x.isFlagged),
			execute: ({ effectiveSelection }) => {
				if (onBeforeModifyEmail()){
					handlers.changeFlagStatus(
						effectiveSelection.filter((x) => x.isFlagged)
							.map((x) => x.emailId),
						MessageFlagState.NotFlagged,
						effectiveSelection[0]
							?.smtpAddress
							?? null
					);
				}
			},
			quickActionIndex: 1
		}
	];
}

export function getResponseActions(
	emailResponse: (
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	) => void
): readonly EmailSelectionAction[] {

	return [
		{
			kind: ActionKind.Regular,
			name: "reply",
			title: localize("_Reply(Verb)"),
			icon: "reply",
			canExecute: ({ effectiveSelection }) => effectiveSelection.length === 1 && !effectiveSelection[0].isDraft,
			execute: ({ effectiveSelection }) => emailResponse(
				effectiveSelection[0].emailId,
				EmailResponseKind.Reply,
				effectiveSelection[0].smtpAddress
			),
		},
		{
			kind: ActionKind.Regular,
			name: "replyAll",
			title: localize("_ReplyAll"),
			icon: "reply-all",
			canExecute: ({ effectiveSelection }) => effectiveSelection.length === 1 && !effectiveSelection[0].isDraft,
			execute: ({ effectiveSelection }) => emailResponse(
				effectiveSelection[0].emailId,
				EmailResponseKind.ReplyAll,
				effectiveSelection[0].smtpAddress
			),
		},
		{
			kind: ActionKind.Regular,
			name: "forward",
			title: localize("_Forward"),
			icon: "share",
			canExecute: ({ effectiveSelection }) => effectiveSelection.length === 1 && !effectiveSelection[0].isDraft,
			execute: ({ effectiveSelection }) => emailResponse(
				effectiveSelection[0].emailId,
				EmailResponseKind.Forward,
				effectiveSelection[0].smtpAddress
			),
		}
	];
}